import { RANGS, type RangId } from "../contract";

/**
 * Les phrases de la renommee dans la regie (ticket 2 du socle) : le bloc « Sa renommee » de la fiche d'un viewer,
 * le classement, la ligne du fil quand quelqu'un monte de rang. Rien du serveur n'est affiche tel quel : un code
 * d'erreur n'a que sa phrase, ecrite ici ; les noms des rangs viennent de l'echelle partagee (`RANGS`), les
 * libelles des motifs d'un gain d'un `Record<Motif, texte>` qui ne compile pas s'il en manque un.
 */

/** Le nom d'un rang, tel que L'Ecuyer l'ecrit (« Écuyer », « Héros »). */
export function nomRang(id: RangId): string {
  return RANGS.find((rang) => rang.id === id)?.nom ?? id;
}

/** Les motifs d'un gain (`renommee_gains.motif`), Twitch d'abord puis Discord. */
export const MOTIFS = [
  "stream",
  "messages",
  "sub",
  "sub_offert",
  "bits",
  "raid",
  "jour_actif",
  "vote",
  "quete",
  "idee",
  "bon_plan",
  "enigme",
  "import",
] as const;
export type Motif = (typeof MOTIFS)[number];

export const LIBELLES_MOTIFS: Record<Motif, string> = {
  stream: "Streams",
  messages: "Messages",
  sub: "Subs",
  sub_offert: "Subs offerts",
  bits: "Bits",
  raid: "Raids",
  jour_actif: "Jours d'écriture",
  vote: "Votes",
  quete: "Quêtes",
  idee: "Idées",
  bon_plan: "Bons plans",
  enigme: "Énigmes",
  import: "Reprise de La Kavalry",
};

/** Le libelle d'un motif ; un motif que ce code ne connait pas encore s'ecrit tel quel (le serveur le borne). */
export function libelleMotif(motif: string): string {
  return Object.hasOwn(LIBELLES_MOTIFS, motif) ? LIBELLES_MOTIFS[motif as Motif] : motif;
}

/** Une renommee, un nombre a la francaise : « 1 284 ». */
const NOMBRE = new Intl.NumberFormat("fr-FR");
export const points = (n: number): string => NOMBRE.format(n);

export const RENOMMEE = {
  titre: "Sa renommée",
  sans: "Écuyer, pas encore de renommée",
  total: (n: number) => `${points(n)} point${n > 1 ? "s" : ""} de renommée`,
  prochain: (reste: number, rang: RangId) => `encore ${points(reste)} avant ${nomRang(rang)}`,
  sommet: "sommet atteint",
  plancher: (rang: RangId) => `Plancher de sa chaîne : ${nomRang(rang)}`,
  detailTitre: "D'où viennent ses points",
  discord: (pseudo: string, depuis: string) => `Relié à Discord : ${pseudo}, depuis le ${depuis}`,
  statsTitre: "Ses stats",
} as const;

export const STATS_LIBELLES = {
  passages: "Passages",
  messages: "Messages",
  moisDeSub: "Mois de sub",
  raids: "Raids menés",
} as const;

export const CLASSEMENT = {
  titre: "Classement de la renommée",
  retour: "Retour au direct",
  recherche: "Chercher",
  recherchePlaceholder: "un pseudo",
  chargement: "Lecture du classement...",
  erreur: "Le classement n'a pas pu être lu. Réessaie dans un instant.",
  horsAcces: "Ce compte n'a pas accès à la régie.",
  vide: "Personne n'a encore de renommée.",
  aucunResultat: "Aucun profil ne correspond.",
  colonnes: { place: "Place", nom: "Pseudo", rang: "Rang", renommee: "Renommée" },
  discordSeul: "Discord seulement",
  total: (n: number) => `${points(n)} profil${n > 1 ? "s" : ""}`,
  precedent: "Page précédente",
  suivant: "Page suivante",
  page: (page: number, pages: number) => `Page ${page} sur ${pages}`,
  ouvrirFiche: (nom: string) => `Ouvrir la fiche de ${nom}`,
  place: (n: number) => (n === 1 ? "1re" : `${n}e`),
} as const;

export const FIL_MONTEE = {
  /** « X devient Chevalier » : le nom est rendu a part, en gras, par le fil. */
  phrase: (rang: RangId) => `devient ${nomRang(rang)}`,
  signe: "↑",
} as const;

export const RANG = {
  etiquette: (rang: RangId) => `Rang : ${nomRang(rang)}`,
} as const;

/** Une date de liaison, en toutes lettres, a l'heure de Paris : « 2 novembre 2026 ». */
const DATE_LONGUE = new Intl.DateTimeFormat("fr-FR", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "Europe/Paris",
});
export const dateDeLiaison = (iso: string): string => DATE_LONGUE.format(new Date(iso));

/** Le geste « Délier » de la fiche : une confirmation, puis une phrase d'un mot choisi par la page, jamais du serveur. */
export const DELIER = {
  bouton: "Délier",
  question: "Délier ces deux comptes ? Chacun garde ses points et le rang atteint ensemble.",
  confirmer: "Oui, délier",
  annuler: "Annuler",
  enCours: "Déliaison...",
  delie: "Comptes déliés.",
  pasRelie: "Ces comptes n'étaient plus reliés.",
  echec: "La liaison n'a pas pu être défaite. Réessaie dans un instant.",
} as const;
